/**
 * Shared rules for the manifest email-intake allowlist.
 *
 * Lives here rather than in the receive-email lambda because three callers need the
 * same answers: the lambda (which address a message resolves against), the API
 * (rejecting a bad entry on write), and Oak (showing the reason inline before the
 * user saves). A second copy of any of these rules would drift.
 */

import type {
  ReceivedEmailRejectionReason,
  ReceivedEmailStatus,
} from "encamp-prisma/generated/browser";

export type EmailIntakeSenderMatchType = "ADDRESS" | "DOMAIN";

/** Release's intake domain. Reaches SES directly, because a forward breaks SPF. */
const RELEASE_INTAKE_DOMAIN = "documents.encamp.com";

/** Where Google forwards `manifests@encamp.com`. See `acceptedIntakeRecipients`. */
const RELEASE_FORWARD_TARGET = "manifests@release.env.encamp.com";

/** The address release published before. Its mail reaches us only through the forward. */
const RELEASE_OLD_PUBLISHED_ADDRESS = "manifests@encamp.com";

/**
 * The one intake address published to customers and shown to staff.
 *
 * `env` is required rather than optional on purpose. An absent env interpolates to
 * "manifests@undefined.env.encamp.com", which looks real enough that staff would
 * hand it to a customer, so callers have to decide what to do about a missing one
 * instead of being handed a plausible lie. The compiler enforces that; this stays a
 * total function so it is safe to call at module scope.
 */
export function intakeEmailAddress(env: string): string {
  const trimmed = env.trim();
  return `manifests@${trimmed === "release" ? RELEASE_INTAKE_DOMAIN : `${trimmed}.env.encamp.com`}`;
}

/**
 * Wider than the address we publish. Release also takes the forward target, where
 * Google-forwarded mail is delivered, and its old published address, which appears
 * only on rows stored with the To/Cc headers as recipients, so replaying one passes.
 */
export function acceptedIntakeRecipients(env: string): string[] {
  const primary = intakeEmailAddress(env);
  if (env.trim() !== "release") return [primary];
  return [primary, RELEASE_FORWARD_TARGET, RELEASE_OLD_PUBLISHED_ADDRESS];
}

/**
 * Every strict parent of a domain, most specific first: "a.b.example.com" yields
 * ["b.example.com", "example.com", "com"].
 *
 * Shared with sender resolution so the allowlist's overlap warnings and the runtime
 * tiering agree on what "a subdomain of" means. Matching a generated parent for
 * equality is what makes it safe — a suffix test would treat evil-example.com as a
 * subdomain of example.com.
 */
export function parentDomainsOf(domain: string): string[] {
  const labels = domain.split(".");
  const parents: string[] = [];
  for (let i = 1; i < labels.length; i++) {
    parents.push(labels.slice(i).join("."));
  }
  return parents;
}

/**
 * Consumer mail providers, which must never be allowlisted at domain granularity —
 * that would trust every account on the provider. Address-level entries for these
 * are fine and genuinely needed: a one-person hauler on Gmail is a real customer.
 */
export const PUBLIC_EMAIL_PROVIDER_DOMAINS = [
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "yahoo.com",
  "ymail.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "proton.me",
  "protonmail.com",
  "pm.me",
  "gmx.com",
  "mail.com",
  "zoho.com",
  "yandex.com",
  "fastmail.com",
] as const;

/**
 * Our own domain. Mail from here that matches no allowlist entry is held for a person
 * to route rather than refused — see `resolveIntakeSender` in the receive-email lambda.
 */
export const ENCAMP_DOMAIN = "encamp.com";

/**
 * Trim, lowercase, drop a display-name wrapper and a leading "@" from domains.
 *
 * Callers should normalize before validating and before storing, so that what was
 * checked is what lands in the column.
 */
export function normalizeSenderValue(matchType: EmailIntakeSenderMatchType, value: string): string {
  let normalized = value.trim().toLowerCase();

  // "Display Name <a@b.com>" — keep only the address.
  const angled = normalized.match(/<([^>]+)>\s*$/);
  if (angled) {
    normalized = angled[1].trim();
  }

  if (matchType === "DOMAIN") {
    normalized = normalized.replace(/^@+/, "");
  }

  return normalized;
}

/** A human-readable reason the value can't be saved, or null when it is acceptable. */
export function validateSenderValue(
  matchType: EmailIntakeSenderMatchType,
  value: string
): string | null {
  const normalized = normalizeSenderValue(matchType, value);

  if (!normalized) {
    return "Enter an email address or domain.";
  }

  if (matchType === "ADDRESS") {
    const parts = normalized.split("@");
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return "Enter a full email address, like dispatch.dallas@vendor.com.";
    }
    if (!parts[1].includes(".")) {
      return "That email address's domain looks incomplete.";
    }
    return null;
  }

  if (normalized.includes("@")) {
    return "Enter a domain only, with no @ — for example vendor.com.";
  }
  if (!normalized.includes(".")) {
    return "Enter a full domain, like vendor.com.";
  }
  if (normalized.startsWith(".") || normalized.endsWith(".") || normalized.includes("..")) {
    return "That domain isn't valid.";
  }
  if (PUBLIC_EMAIL_PROVIDER_DOMAINS.some((provider) => provider === normalized)) {
    return `${normalized} is a public email provider, so allowlisting the whole domain would trust every account on it. Add the specific email address instead.`;
  }
  return null;
}

/**
 * A reason to think twice, which does not stop the save.
 *
 * Separate from `validateSenderValue` because the two answer different questions.
 * Allowlisting a public provider is never right, so that stays a refusal. Allowlisting
 * `encamp.com` as a domain is *usually* wrong — every Encamp employee's forwarded mail
 * would land in one customer's documents, and a staff forward is held for a person to
 * route rather than papered over by a blanket entry — but it is legitimately
 * right for Encamp's own test tenant, which is how this feature is exercised
 * internally.
 *
 * Refusing it outright made such a row impossible to re-save through the
 * dialog, and there is no reliable way to recognise our own tenant: the id differs per
 * environment and no constant names it. So this warns and lets a deliberate choice
 * through, the same way a cross-tenant collision does.
 */
export function adviseOnSenderValue(matchType: "ADDRESS" | "DOMAIN", value: string): string | null {
  if (matchType !== "DOMAIN") return null;
  const normalized = normalizeSenderValue("DOMAIN", value);
  if (normalized !== ENCAMP_DOMAIN) return null;
  return "Every Encamp employee's mail would route to this organization. That is almost always wrong for a customer — add the specific address instead. Correct only for Encamp's own test organization.";
}

// ---------------------------------------------------------------------------
// The staff page
// ---------------------------------------------------------------------------
//
// Where inbound mail is looked at: the Manifest Email tab under Staff → Observe. Oak
// selects the tab on these, the resolver parses the search key, and the held notice
// links to the row — three places that must agree, so they read one definition.

/** The `tab` query parameter that opens the inbound-mail tab. */
/** The shape the tier rule needs of an allowlist entry. */
export type SenderTierCandidate = {
  matchType: EmailIntakeSenderMatchType;
  value: string;
  includeSubdomains: boolean;
};

/**
 * The allowlist entries that decide an address, or none.
 *
 * Precedence is most-specific-first: an exact address beats an exact domain, which
 * beats a subdomain-of match. Only the winning tier is considered — a tier never
 * borrows from a broader one — and the tier is chosen before anything is filtered on
 * whether it is switched on, so a disabled entry refuses the sender outright instead
 * of letting it fall through to a broader entry owned by someone else.
 *
 * Shared because two callers need the same answer: intake, deciding where a message
 * goes, and the staff page, saying which entries are competing for a held one. A
 * second copy would drift and the page would name the wrong organizations.
 */
export function winningSenderTier<T extends SenderTierCandidate>(
  candidates: readonly T[],
  address: string,
  domain: string
): T[] {
  const addressValue = address.toLowerCase();
  const domainValue = domain.toLowerCase();
  const parents = parentDomainsOf(domainValue);
  const tiers: T[][] = [
    candidates.filter((c) => c.matchType === "ADDRESS" && c.value === addressValue),
    candidates.filter((c) => c.matchType === "DOMAIN" && c.value === domainValue),
    candidates.filter(
      (c) => c.matchType === "DOMAIN" && c.includeSubdomains && parents.includes(c.value)
    ),
  ];
  return tiers.find((tier) => tier.length > 0) ?? [];
}

export const MANIFEST_EMAIL_TAB_ID = "manifest-email";

/** The omnisearch key that narrows the inbound grid to one email. */
export const RECEIVED_EMAIL_ID_SEARCH_KEY = "id";

/** The staff page narrowed to one inbound email, as a path with no host. */
export function manifestEmailRowPath(receivedEmailId: string): string {
  const omnisearch = encodeURIComponent(`${RECEIVED_EMAIL_ID_SEARCH_KEY}:${receivedEmailId}`);
  return `/staff/observe?tab=${MANIFEST_EMAIL_TAB_ID}&omnisearch=${omnisearch}`;
}

// ---------------------------------------------------------------------------
// Raw messages
// ---------------------------------------------------------------------------

/**
 * Where SES writes every inbound message: this prefix followed by the SES message id.
 * The prefix is set on the receipt rule in terraform/modules/app/ses.tf and exported
 * there as `intake_email_s3_prefix`. It has never changed and nothing depends on its
 * spelling, so the key is derived here rather than stored per row.
 */
export const INTAKE_RAW_EMAIL_PREFIX = "intake-email-raw/";

export function rawEmailObjectKey(sesMessageId: string): string {
  return `${INTAKE_RAW_EMAIL_PREFIX}${sesMessageId}`;
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------
//
// Running a stored message through intake again. Shared for the same reason the
// allowlist rules are: the API decides whether a row may be replayed and asks for it,
// the lambda recognises the request, and Oak shows or hides the control — three
// callers, one contract.

/**
 * The lambda that runs intake. The API invokes it by this bare name; every
 * environment is its own AWS account, so there is no environment suffix.
 */
export const RECEIVE_EMAIL_FUNCTION_NAME = "receive-email";

/** The five verdicts SES attaches to every inbound receipt. */
export const SES_VERDICTS = ["spam", "virus", "spf", "dkim", "dmarc"] as const;
export type SesVerdict = (typeof SES_VERDICTS)[number];

/**
 * The verdicts intake enforces: a failure refuses the message, and the attachment
 * peek on unplaceable mail runs only when all of them pass.
 *
 * SPF and DKIM are recorded, not enforced: DMARC passes on either one aligned with
 * the From domain, so enforcing either alone refuses senders DMARC accepts.
 *
 * Only DMARC PASS is a pass. GRAY means the domain publishes no DMARC record, so its
 * addresses can be forged freely.
 */
export const ENFORCED_SES_VERDICTS: readonly SesVerdict[] = ["spam", "virus", "dmarc"];

export function isEnforcedSesVerdict(verdict: SesVerdict): boolean {
  return ENFORCED_SES_VERDICTS.includes(verdict);
}

/**
 * A staff request to run one stored message through intake again.
 *
 * Travels in the same shape SES's own notifications do — an SNS envelope inside an
 * SQS record — so the lambda's single entry point handles both and the key
 * `encampReplay` is what tells them apart.
 */
export type EmailReplayRequest = {
  encampReplay: {
    receivedEmailId: string;
    /** Who asked, for the lambda's log line. The row's own bookkeeping is the API's. */
    requestedBy: string;
    /**
     * The organization a staff member chose by hand. When present the lambda files
     * into it without consulting the allowlist — this is how held, ambiguous and
     * errored mail gets routed. Absent for a plain replay.
     */
    tenantOverride?: string;
  };
};

export function isEmailReplayRequest(value: unknown): value is EmailReplayRequest {
  if (typeof value !== "object" || value === null || !("encampReplay" in value)) return false;
  const request = value.encampReplay;
  if (typeof request !== "object" || request === null) return false;
  const override = "tenantOverride" in request ? request.tenantOverride : undefined;
  return (
    "receivedEmailId" in request &&
    typeof request.receivedEmailId === "string" &&
    request.receivedEmailId.length > 0 &&
    "requestedBy" in request &&
    typeof request.requestedBy === "string" &&
    (override === undefined || (typeof override === "string" && override.length > 0))
  );
}

/**
 * The only parts of an SQS record the lambda reads. Typed structurally rather than
 * as `aws-lambda`'s `SQSRecord` so the API, which has no reason to depend on Lambda
 * types, can build one.
 */
export type ReplayInvocationEvent = {
  Records: Array<{ messageId: string; body: string }>;
};

/**
 * The event the API hands the lambda for one replay: an SQS-shaped record whose body
 * is an SNS envelope around the request — exactly how a real receipt arrives.
 *
 * The record id is unique per request rather than per email so two replays of the
 * same message are distinguishable in the lambda's batch-failure report.
 */
export function buildReplayInvocationEvent(request: EmailReplayRequest): ReplayInvocationEvent {
  return {
    Records: [
      {
        messageId: `replay:${request.encampReplay.receivedEmailId}:${Date.now()}`,
        body: JSON.stringify({ Type: "Notification", Message: JSON.stringify(request) }),
      },
    ],
  };
}

/**
 * Refusals a replay can fix, because the fix is ours to make: allowlist the sender,
 * switch its entry back on, or change which verdicts are enforced — a replay re-runs
 * today's rules against the verdicts SES gave on arrival. The rest are properties of
 * the message, which a replay would refuse again for the same reason.
 */
export const REPLAYABLE_REJECTION_REASONS = [
  "SENDER_NOT_ALLOWED",
  "SENDER_DISABLED",
  "SES_VERDICT_FAILED",
] as const satisfies readonly ReceivedEmailRejectionReason[];

/** What the eligibility rules need to know about a row. */
export type ReplayCandidate = {
  status: ReceivedEmailStatus;
  rejectionReason: ReceivedEmailRejectionReason | null | undefined;
  /** Whether any reply row exists for this email. */
  replySent: boolean;
  /** When a replay was last claimed. Lets a claim with no run behind it go again. */
  lastReplayedAt?: Date | string | null;
};

/**
 * Why an action is not offered. A code rather than a sentence: this rule is domain
 * logic shared by the lambda and the API, and the words belong with the surface that
 * shows them.
 */
export type ActionBlock =
  | "ALREADY_ANSWERED"
  | "ROUTE_INSTEAD"
  | "REPLAY_INSTEAD"
  | "REFUSAL_NOT_FIXABLE"
  | "VERDICT_NOT_ROUTABLE"
  | "SENDER_NOT_ALLOWLISTED"
  | "SENDER_SWITCHED_OFF"
  | "STILL_PROCESSING"
  | "ALREADY_SETTLED";

/**
 * How long a row may sit in PROCESSING before its run is treated as dead.
 *
 * Load-bearing rather than a convenience: PROCESSING is the claim, so a lambda killed
 * at its timeout — which runs no catch and no `finally` — would otherwise hold the
 * claim forever and block every future replay of that row. The lambda's own timeout is
 * two minutes and an asynchronous invoke is retried twice with a pause between, so a
 * claim older than this has nothing running behind it.
 */
export const STALE_PROCESSING_MS = 10 * 60 * 1000;

/** A claim old enough that its run can no longer be in flight. */
export function processingIsStale(candidate: ReplayCandidate, now = new Date()): boolean {
  if (candidate.status !== "PROCESSING" || !candidate.lastReplayedAt) return false;
  return now.getTime() - new Date(candidate.lastReplayedAt).getTime() > STALE_PROCESSING_MS;
}

/** Statuses after which the email was handled and the sender told. Nothing to run again. */
export const SETTLED_STATUSES = [
  "FILED",
  "PARTIAL",
  "NOTHING_FILED",
  "NO_ATTACHMENTS",
] as const satisfies readonly ReceivedEmailStatus[];

export function isSettledStatus(status: ReceivedEmailStatus): boolean {
  return SETTLED_STATUSES.some((settled) => settled === status);
}

/** Statuses where no allowlist answer exists and a person has to place the mail. */
export const HELD_STATUSES = [
  "HELD_ENCAMP_SENDER",
  "HELD_AMBIGUOUS",
] as const satisfies readonly ReceivedEmailStatus[];

export function isHeldStatus(status: ReceivedEmailStatus): boolean {
  return HELD_STATUSES.some((held) => held === status);
}

/**
 * Why this email cannot be run through intake again, or null when it can.
 *
 * Read by the API to decide and to say why not, by the lambda to re-check after the
 * network hop, and by Oak only through the API's answer. The rule that matters: never
 * when any acknowledgement was sent, because a run that files sends the normal
 * confirmation and no sender may get two.
 */
export function replayBlocker(candidate: ReplayCandidate): ActionBlock | null {
  switch (candidate.status) {
    // Removing one of the competing entries lets the allowlist decide, which credits
    // the entry and fixes every later message from that sender. Replaying without
    // fixing anything simply holds it again, which costs a click and nothing else.
    case "HELD_AMBIGUOUS":
      return null;
    // A staff forward has no allowlist entry to fix, so a replay would only hold it.
    case "HELD_ENCAMP_SENDER":
      return "ROUTE_INSTEAD";
    case "ERRORED":
      return candidate.replySent ? "ALREADY_ANSWERED" : null;
    case "FAILED_VALIDATION": {
      if (candidate.replySent) return "ALREADY_ANSWERED";
      const reason = candidate.rejectionReason;
      const fixable = reason && REPLAYABLE_REJECTION_REASONS.some((allowed) => allowed === reason);
      return fixable ? null : "REFUSAL_NOT_FIXABLE";
    }
    case "PROCESSING":
      // A stale claim is a run that died before its terminal write. The lambda writes
      // that status before it sends the confirmation, so no reply on a stale row can be
      // from the run that died; the only reply it can carry is a held notice to our own
      // staff, which a re-run answers rather than duplicates. Checking replySent here
      // would leave a died route on a held forward with no way out.
      return processingIsStale(candidate) ? null : "STILL_PROCESSING";
    default:
      return "ALREADY_SETTLED";
  }
}

/**
 * Why this email cannot be routed — run through intake again with a staff member
 * naming the organization — or null when it can.
 *
 * Narrower than replay on purpose. Routing bypasses the allowlist, so it is offered
 * only where the allowlist could not decide: mail held from our own staff, and a
 * sender two organizations both claim where both entries are correct and the
 * ambiguity is permanent. An unknown sender is routed by allowlisting them, which
 * also fixes their next email; a switched-off sender was switched off by someone, and
 * routing around that would defeat the switch; an errored email is replayed, because
 * the fault was ours and the allowlist had already decided.
 */
export function routeBlocker(candidate: ReplayCandidate): ActionBlock | null {
  switch (candidate.status) {
    // Held mail is what routing is for. A held email's one reply row is the notice we
    // sent our own staff, which a route answers rather than duplicates.
    case "HELD_ENCAMP_SENDER":
    case "HELD_AMBIGUOUS":
      return null;
    case "ERRORED":
      return candidate.replySent ? "ALREADY_ANSWERED" : "REPLAY_INSTEAD";
    case "FAILED_VALIDATION": {
      if (candidate.replySent) return "ALREADY_ANSWERED";
      switch (candidate.rejectionReason) {
        case "SENDER_NOT_ALLOWED":
          return "SENDER_NOT_ALLOWLISTED";
        case "SENDER_DISABLED":
          return "SENDER_SWITCHED_OFF";
        // Routing bypasses the allowlist, not the verdict check, which runs on a
        // routed message too. Its own code rather than the catch-all, which says the
        // refusal cannot be re-run — of this one, replay can.
        case "SES_VERDICT_FAILED":
          return "VERDICT_NOT_ROUTABLE";
        default:
          return "REFUSAL_NOT_FIXABLE";
      }
    }
    case "PROCESSING":
      // Same escape as replay: a route that died before its terminal write is routed
      // again, directly, rather than replayed back into a hold first.
      return processingIsStale(candidate) ? null : "STILL_PROCESSING";
    default:
      return "ALREADY_SETTLED";
  }
}
